/**
 * PulseSurveyPage - Public page for employees to submit pulse surveys
 * Accessed via /pulse/{token} — no admin auth required
 */
import React, { useState, useEffect } from 'react';
import {
    MessageSquare, Loader2, AlertTriangle, CheckCircle2,
    Heart, Send
} from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import '../styles/design-system.css';

function PulseSurveyPage({ token }) {
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [answers, setAnswers] = useState({});
    const [wantsToTalk, setWantsToTalk] = useState(false);
    const [talkTopic, setTalkTopic] = useState('');

    useEffect(() => {
        const loadSurvey = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_BASE_URL}/api/public/pulse/${token}`);
                if (res.status === 404) {
                    setError('Survey not found. This link may be invalid.');
                    return;
                }
                if (!res.ok) throw new Error('Failed to load survey');
                const data = await res.json();
                
                if (data.status === 'already_submitted') {
                    setSubmitted(true);
                } else {
                    setSurvey(data);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        loadSurvey();
    }, [token]);

    const handleAnswer = (questionId, answer) => {
        setAnswers(prev => ({ ...prev, [questionId]: answer }));
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const responses = Object.entries(answers).map(([question_id, answer]) => ({
                question_id,
                answer
            }));

            const res = await fetch(`${API_BASE_URL}/api/public/pulse/${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    responses,
                    wants_to_talk: wantsToTalk,
                    talk_topic: wantsToTalk ? talkTopic : null
                })
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.detail || 'Failed to submit');
            }

            setSubmitted(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="pulse-page">
                <div className="pulse-page-loading">
                    <Loader2 size={32} className="rp-spinner-icon" />
                    <p>Loading survey...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pulse-page">
                <div className="pulse-page-error">
                    <AlertTriangle size={32} style={{ color: '#FF3B30' }} />
                    <h2>Unable to Load Survey</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="pulse-page">
                <div className="pulse-page-success">
                    <CheckCircle2 size={48} style={{ color: '#34C759' }} />
                    <h2>Thank You!</h2>
                    <p>Your feedback has been recorded. Your responses are anonymous and help improve the workplace.</p>
                </div>
            </div>
        );
    }

    if (!survey) return null;

    const questions = survey.questions || [];
    const allAnswered = questions.every(q => answers[q.id]);

    return (
        <div className="pulse-page">
            <div className="pulse-page-container">
                <header className="pulse-page-header">
                    <MessageSquare size={24} style={{ color: '#007AFF' }} />
                    <h1>Quick Pulse Check</h1>
                    <p>Hi {survey.employee_name || 'there'}! Please take a moment to answer these quick questions.</p>
                </header>

                <div className="pulse-questions">
                    {questions.map((q, i) => (
                        <div key={q.id} className="pulse-question-card">
                            <h3>{i + 1}. {q.question}</h3>
                            <div className="pulse-answer-buttons">
                                <button
                                    className={`pulse-answer-btn ${answers[q.id] === 'yes' ? 'pulse-answer-yes-active' : ''}`}
                                    onClick={() => handleAnswer(q.id, 'yes')}
                                >
                                    <CheckCircle2 size={16} /> Yes
                                </button>
                                <button
                                    className={`pulse-answer-btn ${answers[q.id] === 'no' ? 'pulse-answer-no-active' : ''}`}
                                    onClick={() => handleAnswer(q.id, 'no')}
                                >
                                    <AlertTriangle size={16} /> No
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Talk to Someone */}
                <div className="pulse-talk-section">
                    <label className="pulse-talk-toggle">
                        <input
                            type="checkbox"
                            checked={wantsToTalk}
                            onChange={(e) => setWantsToTalk(e.target.checked)}
                        />
                        <Heart size={16} style={{ color: wantsToTalk ? '#FF2D55' : '#86868B' }} />
                        <span>I'd like to talk to someone about how things are going</span>
                    </label>
                    {wantsToTalk && (
                        <textarea
                            className="pulse-talk-textarea"
                            placeholder="What would you like to discuss? (optional)"
                            value={talkTopic}
                            onChange={(e) => setTalkTopic(e.target.value)}
                            maxLength={1000}
                            rows={3}
                        />
                    )}
                </div>

                {/* Submit */}
                <button
                    className="pulse-submit-btn"
                    onClick={handleSubmit}
                    disabled={!allAnswered || submitting}
                >
                    {submitting ? (
                        <><Loader2 size={16} className="rp-spinner-icon" /> Submitting...</>
                    ) : (
                        <><Send size={16} /> Submit Feedback</>
                    )}
                </button>
            </div>
        </div>
    );
}

export default PulseSurveyPage;
